import { type ReactElement } from "react";
import { useMutation } from "@tanstack/react-query";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import type { Strategy } from "@/types/strategy";
import { TokenRevealDialog } from "./TokenRevealDialog";

export type RevealState = { token: string; title: string; description: string } | null;

export interface DialogFlags {
  kill: boolean;
  del: boolean;
}

export interface MutHooks {
  killMut: ReturnType<typeof useMutation<Strategy, unknown, boolean>>;
  deleteMut: ReturnType<typeof useMutation<void, unknown, void>>;
}

interface DetailDialogsProps {
  strategy: Strategy;
  flags: DialogFlags;
  setFlag: (k: keyof DialogFlags, v: boolean) => void;
  muts: MutHooks;
  reveal: RevealState;
  setReveal: (r: RevealState) => void;
}

export function DetailDialogs({
  strategy,
  flags,
  setFlag,
  muts,
  reveal,
  setReveal,
}: Readonly<DetailDialogsProps>): ReactElement {
  return (
    <>
      <KillConfirm
        open={flags.kill}
        strategy={strategy}
        mut={muts.killMut}
        onCancel={() => {
          setFlag("kill", false);
        }}
      />
      <DeleteConfirm
        open={flags.del}
        strategy={strategy}
        mut={muts.deleteMut}
        onCancel={() => {
          setFlag("del", false);
        }}
      />
      <TokenRevealDialog
        open={reveal !== null}
        token={reveal?.token ?? null}
        title={reveal?.title ?? ""}
        description={reveal?.description ?? ""}
        onClose={() => {
          setReveal(null);
        }}
      />
    </>
  );
}

interface KillProps {
  open: boolean;
  strategy: Strategy;
  mut: MutHooks["killMut"];
  onCancel: () => void;
}

function KillConfirm({ open, strategy, mut, onCancel }: Readonly<KillProps>): ReactElement {
  const on = strategy.kill_switch;
  return (
    <ConfirmDialog
      open={open}
      title={on ? "Re-enable strategy" : "Turn kill switch on"}
      description={killDescription(strategy)}
      confirmLabel={on ? "Re-enable" : "Enable kill switch"}
      icon={on ? "ti-player-play" : "ti-player-pause"}
      tone="warning"
      busy={mut.isPending}
      onCancel={onCancel}
      onConfirm={() => {
        mut.mutate(!on);
      }}
    />
  );
}

function killDescription(s: Strategy): string {
  if (s.kill_switch) {
    return `Re-enable ${s.name}? New ENTRY alerts will be accepted again.`;
  }
  return `New ENTRY alerts for ${s.name} will be rejected until you turn it off. Existing open positions keep running (trailing SL still active).`;
}

interface DeleteProps {
  open: boolean;
  strategy: Strategy;
  mut: MutHooks["deleteMut"];
  onCancel: () => void;
}

function DeleteConfirm({ open, strategy, mut, onCancel }: Readonly<DeleteProps>): ReactElement {
  return (
    <ConfirmDialog
      open={open}
      variant="type-to-confirm"
      expected={strategy.name}
      title="Delete strategy"
      description={<DeleteCopy name={strategy.name} />}
      confirmLabel="Delete strategy"
      icon="ti-trash"
      tone="danger"
      busy={mut.isPending}
      onCancel={onCancel}
      onConfirm={() => {
        mut.mutate();
      }}
    />
  );
}

function DeleteCopy({ name }: Readonly<{ name: string }>): ReactElement {
  return (
    <span>
      This will permanently delete <span className="font-medium text-ink">{name}</span> and all its
      trade history. The webhook URL stops working immediately - TradingView alerts will fail.
    </span>
  );
}
